import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { StoreCharacterType, StoreListResponse, getStoreCharacters, purchaseCharacter, PurchaseResponse } from '../api/store';
import '../styles/global.css';

const Store: React.FC = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [storeData, setStoreData] = useState<StoreListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [selectedType, setSelectedType] = useState<StoreCharacterType | null>(null);
  const [characterName, setCharacterName] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState<PurchaseResponse | null>(null);

  useEffect(() => {
    const fetchStoreCharacters = async () => {
      try {
        const data = await getStoreCharacters();
        setStoreData(data);
      } catch (err) {
        setError('Failed to load store characters');
      } finally {
        setLoading(false);
      }
    };
    fetchStoreCharacters();
  }, []);

  const handleBuyClick = (charType: StoreCharacterType) => {
    setSelectedType(charType);
    setCharacterName('');
    setPurchaseSuccess(null);
    setError('');
  };

  const handleCloseModal = () => {
    setSelectedType(null);
    setCharacterName('');
    setPurchaseSuccess(null);
  };

  const handlePurchase = async () => {
    if (!selectedType) return;

    const trimmedName = characterName.trim();
    if (trimmedName.length < 1 || trimmedName.length > 20) {
      setError('Character name must be 1-20 characters');
      return;
    }

    setPurchasing(true);
    setError('');

    try {
      const result = await purchaseCharacter(selectedType.id, trimmedName);
      if (result.success) {
        setPurchaseSuccess(result);
        await refreshUser();
        // Refresh store data to update can_afford
        const data = await getStoreCharacters();
        setStoreData(data);
      } else {
        setError(result.message || 'Purchase failed');
      }
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Purchase failed';
      setError(message);
    } finally {
      setPurchasing(false);
    }
  };

  const handleGoToAccount = () => {
    navigate('/account');
  };

  if (loading) {
    return <div className="loading">Loading store...</div>;
  }

  return (
    <div className="page">
      <nav className="navbar">
        <div className="nav-brand">Block Math</div>
        <div className="nav-links">
          <span className="coin-display">{storeData?.player_coins ?? 0} Coins</span>
          <button className="nav-btn nav-btn-secondary" onClick={() => navigate('/account')}>
            Back
          </button>
        </div>
      </nav>

      <div className="container">
        <div className="store-header">
          <h1>Store</h1>
          <p className="subtitle">Spend your coins on new characters</p>
        </div>

        {error && !selectedType && <div className="error">{error}</div>}

        <div className="store-items">
          {storeData?.character_types.map((charType) => (
            <div key={charType.id} className={`store-item-card ${!charType.can_afford ? 'disabled' : ''}`}>
              <div className="store-item-avatar">
                <img src={charType.avatar_url} alt={charType.name} />
              </div>
              <h3 className="store-item-name">{charType.name}</h3>
              <p className="store-item-description">{charType.description}</p>
              <div className="store-item-price">{charType.price} Coins</div>
              <button
                className="btn btn-primary"
                onClick={() => handleBuyClick(charType)}
                disabled={!charType.can_afford}
              >
                {charType.can_afford ? 'Buy' : 'Not enough coins'}
              </button>
            </div>
          ))}

          {(!storeData?.character_types || storeData.character_types.length === 0) && (
            <p className="placeholder-text">No characters available in the store</p>
          )}
        </div>
      </div>

      {/* Purchase Modal */}
      {selectedType && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {purchaseSuccess ? (
              <>
                <h2>Purchase Successful!</h2>
                <p className="success-message">
                  You now have a new character: <strong>{purchaseSuccess.character?.name}</strong>
                </p>
                <p className="remaining-coins">
                  Remaining coins: {purchaseSuccess.remaining_coins}
                </p>
                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={handleGoToAccount}>
                    Go to Account
                  </button>
                  <button className="btn btn-secondary" onClick={handleCloseModal}>
                    Continue Shopping
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>Purchase {selectedType.name}</h2>
                <p className="modal-subtitle">
                  This will cost <strong>{selectedType.price} coins</strong>
                </p>

                {/* Show character type avatar */}
                <div className="avatar-preview-simple">
                  <img src={selectedType.avatar_url} alt={selectedType.name} />
                </div>

                {selectedType.description && (
                  <p className="type-description">{selectedType.description}</p>
                )}

                <div className="form-group">
                  <label htmlFor="characterName">
                    Character Name ({characterName.trim().length}/20)
                  </label>
                  <input
                    type="text"
                    id="characterName"
                    value={characterName}
                    onChange={(e) => setCharacterName(e.target.value)}
                    placeholder="Enter character name"
                    maxLength={20}
                    disabled={purchasing}
                    required
                  />
                </div>

                {error && <div className="error">{error}</div>}

                <div className="modal-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handlePurchase}
                    disabled={purchasing || !characterName.trim()}
                  >
                    {purchasing ? 'Purchasing...' : `Purchase for ${selectedType.price} coins`}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={purchasing}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Store;
